import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSession } from '../../state/session.tsx';
import { useDocumentTitle } from '../../hooks/index.ts';
import { LoadingBlock, PageHeader } from '../ui.tsx';

/**
 * The frame a Settings sub-page shares: the way back up a level, the page's
 * title, and one line on what it holds.
 *
 * Every card mounted inside reads the company from the session and renders
 * nothing until it is there, so the frame waits for it too — a header over an
 * empty page reads as "this setting is gone" on a slow connection.
 */
export function SettingsLeafPage({
  title,
  description,
  back,
  children,
}: {
  title: string;
  description?: ReactNode;
  back: { to: string; label: string };
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const { company } = useSession();
  useDocumentTitle(title);

  return (
    <>
      <p className="mb-2 text-sm">
        <Link className="font-semibold text-[color:var(--color-brand)] hover:underline" to={back.to}>
          {back.label}
        </Link>
      </p>
      <PageHeader title={title} description={description} />
      {company ? children : <LoadingBlock label={t('settings.loadingSettings')} />}
    </>
  );
}

/**
 * One destination on a Settings hub page: a card-sized link with a line on
 * what is behind it, and a warning badge when something there is waiting on
 * the owner. The badge is inside the link, so it is part of its name.
 */
export function SettingsHubTile({
  to,
  title,
  description,
  badge = null,
}: {
  to: string;
  title: string;
  description: string;
  badge?: string | null;
}) {
  return (
    <li>
      <Link
        to={to}
        className="card block h-full hover:border-[color:var(--color-brand)] focus-visible:outline-2 focus-visible:outline-offset-2"
      >
        <span className="flex flex-wrap items-center gap-2">
          <span className="font-semibold">{title}</span>
          {badge ? (
            <span className="badge border-[color:var(--color-warning)] text-[color:var(--color-warning)]">{badge}</span>
          ) : null}
        </span>
        <span className="mt-1 block text-sm text-[color:var(--color-muted)]">{description}</span>
      </Link>
    </li>
  );
}
